import {
  Box,
  Image,
  Stack,
  AspectRatio,
  Group,
  Text,
  Anchor,
} from "@mantine/core"
import type { BoxProps } from "@mantine/core"
import { DefaultModal } from "./default-modal"
import { DownloadFileButton } from "./DownloadFileButton"
import { getFileType } from "@/utils/getFileType"
import { ToggleMaximizeActionIcon } from "./ToggleMaximizeActionIcon"

export interface FilePreviewProps extends BoxProps {
  src: string
  fileName: string
  withDownload?: boolean
  onLoad?: React.ReactEventHandler<
    HTMLImageElement | HTMLVideoElement | HTMLIFrameElement | HTMLAudioElement
  >
}

const previewTypes = ["image", "video", "audio"]

export function FilePreview({
  src,
  fileName,
  withDownload = true,
  onLoad,
  ...boxProps
}: FilePreviewProps): JSX.Element {
  const [opened, setOpened] = React.useState(false)
  const fileType = getFileType(fileName)
  const isPreviewType = previewTypes.includes(fileType)
  const DownloadIcon = React.memo(
    () =>
      withDownload && (
        <DownloadFileButton variant="icon" src={src} fileName={fileName} />
      )
  )

  function renderPreview(inModal = false) {
    const commonStyle = inModal
      ? { width: "100%", maxHeight: "90vh" as const }
      : { width: "100%", height: "100%" as const }

    switch (fileType) {
      case "image":
        return (
          <AspectRatio ratio={4 / 3}>
            <Image
              src={src}
              onLoad={onLoad}
              alt={fileName}
              fit="contain"
              style={commonStyle}
            />
          </AspectRatio>
        )
      case "video":
        return <video src={src} controls onLoad={onLoad} style={commonStyle} />
      case "audio":
        return (
          <audio src={src} controls onLoad={onLoad} style={{ width: "100%" }} />
        )
      case "pdf":
        return (
          <Stack h={"100%"}>
            <iframe
              src={src}
              datatype="application/pdf"
              width={"100%"}
              height={"100%"}
            ></iframe>
            <Group px={5} pb={25} justify="space-between" wrap="nowrap">
              <Anchor href={src} target="_blank">
                {fileName}
              </Anchor>
              <DownloadIcon />
            </Group>
          </Stack>
        )
      default:
        return (
          <Group justify="space-between" wrap="nowrap">
            <Text>{fileName}</Text>
            <DownloadIcon />
          </Group>
        )
    }
  }

  const [maximized, setMaximized] = React.useState(false)

  function toggleMaximize(newMaximized: boolean) {
    setMaximized(newMaximized)
  }

  return (
    <>
      <Box
        {...boxProps}
        onClick={() => isPreviewType && setOpened(true)}
        style={{
          cursor: isPreviewType ? "pointer" : undefined,
          overflow: "hidden",
          borderRadius: 8,
          ...boxProps.style,
        }}
      >
        {/* <AspectRatio ratio={4 / 3}>{renderPreview()}</AspectRatio> */}
        {renderPreview()}
      </Box>

      <DefaultModal
        opened={opened}
        onClose={() => setOpened(false)}
        // size="auto"
        size={"xl"}
        fullScreen={maximized}
        centered
        title={
          <Group>
            <ToggleMaximizeActionIcon onClick={toggleMaximize} />
            <DownloadIcon />
          </Group>
        }
      >
        <Stack>{renderPreview(true)}</Stack>
      </DefaultModal>
    </>
  )
}
